import { useParams, Link } from "react-router-dom";
import { ArrowRight, MapPin, Quote, Sunrise, Sun, Moon } from "lucide-react";

// Curated imagery per collection (client-provided, Google Drive CDN)
const IMG = (id, w = 1800) => `https://lh3.googleusercontent.com/d/${id}=w${w}`;

const DATA = {
  community: {
    name: "Tarzan Community",
    tagline: "Rooted retreats built with — and for — the neighbourhood.",
    accent: "bg-sand-100",
    accentText: "text-forest-800",
    hero:  IMG("15o0aiHe3XO-R-Qe-QZMrIB1WlxTBD_9A", 1900),
    hero2: IMG("1W5bd9I-peA1ZyOsk1ZQEk52cRJcNM2rL", 1400),
    intro: "Tarzan Community properties are anchored by their neighbourhoods. Every ingredient on the plate, every artisan behind the interiors, every guide who walks you through the forest — everyone comes from within a hundred-kilometre radius. Every rupee spent by our guests stays in the ecosystem that hosts them.",
    stats: [
      ["100km", "sourcing radius for every property"],
      ["80%+", "of the crew is homegrown to the village"],
      ["12", "artisan families we co-own our interiors with"],
    ],
    features: [
      ["Homegrown crew", "Every staff member lives within an hour of the retreat and is trained on-site over a six-month apprenticeship."],
      ["Artisan interiors", "Furniture, textiles, ceramics and craft — commissioned from village artisans, with attribution on every piece."],
      ["Farm partners", "80%+ of daily produce comes from 6–8 partner farms within a 40 km circle around the property."],
      ["Cultural evenings", "Local musicians, folk artists and storytellers curate two evenings a week — free for house guests."],
      ["Craft workshops", "Pottery, block-printing, weaving and cooking sessions hosted by the makers themselves."],
      ["Give-back baked in", "5% of every night booked is deposited into a village trust the community itself controls."],
    ],
    day: {
      morning: ["Sunrise walk to the village well", "A pot of filter kaapi with the elders", "Fresh-milled ragi dosa breakfast"],
      afternoon: ["Ride to the artisan cluster", "Try your hand at the potter's wheel", "Lunch at the community kitchen"],
      evening: ["Folk music on the courtyard", "Wood-fired dinner under lanterns", "Bonfire storytelling with the guide"],
    },
    gallery: [
      IMG("15o0aiHe3XO-R-Qe-QZMrIB1WlxTBD_9A", 1400),
      IMG("1W5bd9I-peA1ZyOsk1ZQEk52cRJcNM2rL", 1200),
      IMG("1mxRCSg25BWwOqoclwHL2ufP45I94FS7s", 1200),
      IMG("1sxH9v-b_Tc0HB1MZ1MUYgf3_zIyR7h45", 1200),
    ],
    testimonial: {
      quote: "It didn't feel like we were staying at a resort — it felt like the village had opened its door and cooked for us for four days.",
      author: "Meera & Aditya, Bangalore",
      trip: "3 nights · Tarzan Community · Chikmagalur pilot",
    },
    coming_soon_list: ["Coorg", "Wayanad", "Chikmagalur", "Araku Valley"],
  },
  farmstay: {
    name: "Tarzan Farmstay",
    tagline: "Working farms opened up for slow, sunlit stays.",
    accent: "bg-forest-50",
    accentText: "text-forest-800",
    hero:  IMG("1aqwimeRWVRvBrO0K0z2B3jYJradfoyMY", 1900),
    hero2: IMG("1ELduEHdzKI4fe8eayE0Kc3hlymJyHtBc", 1400),
    intro: "Wake up on a working coffee, spice or fruit farm. Walk the plantation at sunrise. Help harvest something for lunch. Sit down to a table where every single ingredient travelled less than a hundred metres to reach you. Tarzan Farmstay opens up livelihoods, not stage-sets.",
    stats: [
      ["6–24", "guests only, ever, per farm"],
      ["100m", "average distance from field to plate"],
      ["3rd", "or 4th generation farming families"],
    ],
    features: [
      ["Real, working farms", "These are not manicured props — they are livelihoods, opened up for a handful of guests at a time."],
      ["Chef on the farm", "A private chef lives on-site and cooks the day's harvest for every meal. No menus, no repeats."],
      ["Farm-to-glass bar", "Cocktails and mocktails built from what's ripe that week — jamun shrubs, cold-brew coffee, kokum coolers."],
      ["Learn the craft", "Coffee roasting, spice grading, honey harvesting — guided personally by the farm's owner."],
      ["Wellness cabin", "A private wellness cabin on the farm with steam, ayurveda massage and outdoor showers."],
      ["Star-lit dining", "One-table plantation dinners under fairy lights, hosted by the family."],
    ],
    day: {
      morning: ["Sunrise walk through the coffee estate", "Pluck ripe cherries with the pickers", "Breakfast on the plantation deck"],
      afternoon: ["Coffee roasting & cupping session", "Long lunch cooked with the morning's harvest", "Nap in a hammock under jackfruit trees"],
      evening: ["Sundowner shrub cocktails on the veranda", "Wood-fired dinner served family-style", "Firefly walk back to your cabin"],
    },
    gallery: [
      IMG("1aqwimeRWVRvBrO0K0z2B3jYJradfoyMY", 1400),
      IMG("1ELduEHdzKI4fe8eayE0Kc3hlymJyHtBc", 1200),
      IMG("1v2FrLk41BCN-LBOiCXs4cGC-pxcurD5S", 1200),
      IMG("1ELduEHdzKI4fe8eayE0Kc3hlymJyHtBc", 1000),
    ],
    testimonial: {
      quote: "The coffee we sipped at 6am had been on the plant twelve hours earlier. That's the entire brief of Tarzan Farmstay in one cup.",
      author: "Rohan & Priya, Mumbai",
      trip: "4 nights · Tarzan Farmstay · Sakleshpur pilot",
    },
    coming_soon_list: ["Sakleshpur", "Chikmagalur", "Mahabaleshwar", "Munnar"],
  },
  villa: {
    name: "Tarzan Villa",
    tagline: "Architecture-led private villas for milestone escapes.",
    accent: "bg-ivory",
    accentText: "text-forest-800",
    hero:  IMG("1vZVBrym_Gjxc_Jh2XZWDuG4ilkezTaEM", 1900),
    hero2: IMG("1pECItp5h_wGorLAAE8ke1_JpILbqRIW3", 1400),
    intro: "Tarzan Villa is our architectural imprint — private pool decks, floor-to-ceiling glass, hand-picked art, live-in chef and a dedicated concierge. Designed for weddings, milestone birthdays and the kind of once-a-year escape you plan the whole calendar around.",
    stats: [
      ["1", "villa. yours entirely."],
      ["24/7", "concierge, house manager & chef"],
      ["8–14", "guests, quietly, without ever sharing a wall"],
    ],
    features: [
      ["Whole-home privacy", "You take the whole villa for your group. No shared walls, no other guests, no reception."],
      ["Private pool + deck", "Every villa comes with a heated private pool and a sun deck curated for lounging and dinners."],
      ["Live-in chef", "Your chef arrives before you do. Menus are built around your preferences and what's in season."],
      ["Concierge team", "House manager, housekeeping and dedicated driver — assigned only to your booking."],
      ["Milestone-ready", "Weddings, birthdays, anniversaries — handled end-to-end by our celebrations lead."],
      ["Signature design", "Every villa is designed with a lead architect — no cookie-cutter templates, no shared vocabulary."],
    ],
    day: {
      morning: ["Wake to the sun on the deck", "Breakfast served at your pool", "Yoga with the resident instructor"],
      afternoon: ["Long lounge and swim", "In-villa spa with visiting therapist", "Board games in the lounge"],
      evening: ["Bespoke dinner by your chef", "Bonfire, live music on request", "Nightcap on the deck"],
    },
    gallery: [
      IMG("1vZVBrym_Gjxc_Jh2XZWDuG4ilkezTaEM", 1400),
      IMG("1pECItp5h_wGorLAAE8ke1_JpILbqRIW3", 1200),
      IMG("1Jz2xHq127g2LtC7s-dObbx5W4JkGZhKN", 1200),
      IMG("1_TJKPoQ5YWnOt8L4S4SFqUkvcGzdsTeT", 1200),
    ],
    testimonial: {
      quote: "We booked it for a 40th. We got a wedding-week venue, a resident chef, and the quietest four mornings of the year.",
      author: "Kavya & Arjun, Delhi",
      trip: "4 nights · Tarzan Hyderabad Retreat · Nov 2025",
    },
    coming_soon_list: ["Udaipur", "Lonavala", "Alibaug", "Goa"],
  },
  village: {
    name: "Tarzan Village",
    tagline: "Small hamlets of cabins around a shared central lodge.",
    accent: "bg-forest-100",
    accentText: "text-forest-800",
    hero:  IMG("1JPd3BLPd_sm-G24ZAHMP3WcjcEDAs2yY", 1900),
    hero2: IMG("1EO8v8xr3-llQ4QwwKVOSJZrNfrL1oq7p", 1400),
    intro: "Tarzan Village is a cluster of six to twelve private cabins arranged around a shared lodge, pool and dining pavilion. Purpose-built for extended families, wellness cohorts and small corporate offsites — the intimacy of a private stay with the energy of a village-square evening.",
    stats: [
      ["6–12", "private cabins per village"],
      ["20–40", "guests hosted at a time"],
      ["1", "shared lodge, kitchen and pool"],
    ],
    features: [
      ["Private cabins", "Every cabin sleeps 2–4 with its own bathroom, deck, wardrobe and view. No dorms, ever."],
      ["Shared lodge", "A central pavilion for meals, evening drinks and rainy afternoons — the beating heart of the village."],
      ["Group-ready", "20–40 guests hosted comfortably, with the intimacy and privacy of a boutique stay."],
      ["Full programming", "Yoga, guided walks, workshops and evening bonfires — curated per group, per trip."],
      ["Wellness pavilion", "Steam, sauna, spa treatments and a resident wellness lead on every village."],
      ["Kids' corner", "Age-appropriate activities, a small library and safe play spaces — parents can actually rest."],
    ],
    day: {
      morning: ["Village-wide yoga on the lawn", "Communal breakfast at the lodge", "Optional guided nature walk"],
      afternoon: ["Poolside lunch buffet", "Choose your own — workshop, walk or nap", "Sundowner tea at the lodge"],
      evening: ["Village bonfire and drinks", "Family-style dinner at the pavilion", "Optional stargazing on the lawn"],
    },
    gallery: [
      IMG("1JPd3BLPd_sm-G24ZAHMP3WcjcEDAs2yY", 1400),
      IMG("1EO8v8xr3-llQ4QwwKVOSJZrNfrL1oq7p", 1200),
      IMG("12X5y49BreUAjm0dH2ZIpajvR1wtwk_-A", 1200),
      IMG("1sduH3nC8ZpYHxUhfMD7pa4Y5hUMsGBXj", 1200),
    ],
    testimonial: {
      quote: "Eighteen of us, three generations, one week — and somehow every single person got their own quiet corner and their own good time.",
      author: "The Menon family, Chennai",
      trip: "6 nights · Tarzan Village · Rishikesh pilot",
    },
    coming_soon_list: ["Rishikesh", "Manali", "Kasauli", "Ooty"],
  },
};

function StatBlock({ stats }) {
  return (
    <div className="grid grid-cols-3 gap-6 md:gap-12">
      {stats.map(([n, l]) => (
        <div key={l} className="border-l-2 border-forest/60 pl-4 md:pl-6">
          <div className="font-serif text-3xl md:text-5xl text-forest tracking-tight num">{n}</div>
          <div className="text-xs md:text-sm text-ink-soft mt-2 leading-snug">{l}</div>
        </div>
      ))}
    </div>
  );
}

function DayColumn({ icon: Icon, title, items }) {
  return (
    <div className="bg-white/70 backdrop-blur border border-forest/10 rounded-2xl p-6 md:p-7">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-10 w-10 rounded-full bg-forest/10 flex items-center justify-center">
          <Icon className="h-5 w-5 text-forest" />
        </div>
        <div className="eyebrow">{title}</div>
      </div>
      <ul className="space-y-3">
        {items.map(it => (
          <li key={it} className="text-ink-soft text-sm leading-relaxed flex gap-3">
            <span className="text-forest">—</span><span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function RetreatCategory() {
  const { slug } = useParams();
  const d = DATA[slug];
  if (!d) return (
    <div className="pt-40 pb-24 container-tarzan text-center">
      <h1 className="font-serif text-4xl text-ink">Collection not found.</h1>
      <Link to="/" className="mt-6 inline-flex items-center gap-2 text-forest">Back to home <ArrowRight className="h-4 w-4"/></Link>
    </div>
  );
  const isVilla = slug === "villa";

  return (
    <div className="bg-ivory" data-testid={`retreat-category-${slug}`}>
      {/* Hero */}
      <section className="relative h-[80vh] min-h-[560px] overflow-hidden">
        <img src={d.hero} alt={d.name} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-forest-900/95 via-forest-900/50 to-forest-900/20" />
        <div className="relative container-tarzan h-full flex flex-col justify-end pb-16 md:pb-24 text-ivory">
          <div className="eyebrow text-sand-200 mb-4">The Collection</div>
          <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl leading-[1.02] tracking-tight max-w-5xl">{d.name}</h1>
          <p className="mt-5 text-ivory/85 max-w-2xl text-base md:text-xl leading-relaxed">{d.tagline}</p>
        </div>
      </section>

      {/* Intro + Stats */}
      <section className={`py-20 md:py-28 ${d.accent}`}>
        <div className="container-tarzan grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-7">
            <div className="eyebrow mb-4">What it is</div>
            <p className="text-ink text-lg md:text-2xl leading-relaxed font-serif tracking-tight">{d.intro}</p>
          </div>
          <div className="lg:col-span-5 lg:pt-4">
            <StatBlock stats={d.stats} />
          </div>
        </div>
      </section>

      {/* Signature features */}
      <section className="py-20 md:py-24">
        <div className="container-tarzan">
          <div className="max-w-2xl mb-10">
            <div className="eyebrow mb-3">The signature</div>
            <h2 className="font-serif text-4xl md:text-5xl text-ink tracking-tight leading-[1.05]">What makes this collection its own thing.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {d.features.map(([h, b], idx) => (
              <div key={h} className="bg-white border border-forest/10 rounded-2xl p-7 soft-shadow hover:luxe-shadow transition-all">
                <div className="text-xs uppercase tracking-[0.22em] text-forest font-medium mb-3 num">0{idx + 1}</div>
                <div className="font-serif text-xl md:text-2xl text-ink leading-tight">{h}</div>
                <p className="text-ink-soft text-sm mt-3 leading-relaxed">{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* A day in the life */}
      <section className="py-20 md:py-24 bg-forest-900 text-ivory relative overflow-hidden">
        <div className="absolute inset-0 opacity-25">
          <img src={d.hero2} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-forest-900/70 via-forest-900/85 to-forest-900" />
        <div className="relative container-tarzan">
          <div className="max-w-2xl mb-12">
            <div className="eyebrow text-sand-200 mb-3">A day in the life</div>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight">One day at {d.name}, roughly.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <DayColumn icon={Sunrise} title="Morning" items={d.day.morning} />
            <DayColumn icon={Sun} title="Afternoon" items={d.day.afternoon} />
            <DayColumn icon={Moon} title="Evening" items={d.day.evening} />
          </div>
        </div>
      </section>

      {/* Gallery bento */}
      <section className="py-20 md:py-24">
        <div className="container-tarzan">
          <div className="max-w-2xl mb-10">
            <div className="eyebrow mb-3">The gallery</div>
            <h2 className="font-serif text-4xl md:text-5xl text-ink tracking-tight leading-[1.05]">A closer look.</h2>
          </div>
          <div className="grid grid-cols-4 grid-rows-2 gap-4 md:gap-5 h-[480px] md:h-[640px]">
            <div className="col-span-4 md:col-span-2 row-span-2 rounded-3xl overflow-hidden">
              <img src={d.gallery[0]} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1200ms]" />
            </div>
            <div className="col-span-2 row-span-1 rounded-2xl overflow-hidden hidden md:block">
              <img src={d.gallery[1]} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1200ms]" />
            </div>
            <div className="col-span-1 md:col-span-1 row-span-1 rounded-2xl overflow-hidden hidden md:block">
              <img src={d.gallery[2]} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1200ms]" />
            </div>
            <div className="col-span-1 md:col-span-1 row-span-1 rounded-2xl overflow-hidden hidden md:block">
              <img src={d.gallery[3]} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1200ms]" />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className={`py-20 md:py-24 ${d.accent}`}>
        <div className="container-tarzan max-w-4xl">
          <Quote className="h-10 w-10 text-forest/40 mb-6" />
          <blockquote className="font-serif text-2xl md:text-4xl text-ink leading-[1.25] tracking-tight text-balance">
            &ldquo;{d.testimonial.quote}&rdquo;
          </blockquote>
          <div className="mt-8 flex items-center gap-4">
            <div className="h-px w-12 bg-forest" />
            <div>
              <div className="font-serif text-lg text-ink">{d.testimonial.author}</div>
              <div className="text-xs uppercase tracking-[0.22em] text-ink-soft mt-1">{d.testimonial.trip}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Availability */}
      <section className="py-20 md:py-28">
        <div className="container-tarzan text-center max-w-2xl mx-auto">
          <div className="eyebrow mb-3">Availability</div>
          {isVilla ? (
            <>
              <h2 className="font-serif text-4xl md:text-5xl text-ink tracking-tight leading-[1.05]">Now bookable in Hyderabad.</h2>
              <p className="text-ink-soft mt-4 leading-relaxed text-base md:text-lg">Our flagship villa is open for reservations today. Other locations below are joining shortly.</p>
              <Link data-testid="cat-cta-book" to="/stays/tarzan-hyderabad-retreat" className="inline-flex items-center gap-2 mt-8 bg-forest text-ivory px-8 py-4 rounded-full text-sm font-medium tracking-wide hover:bg-forest-700 transition-all">
                Book Tarzan Hyderabad Retreat <ArrowRight className="h-4 w-4"/>
              </Link>
            </>
          ) : (
            <>
              <h2 className="font-serif text-4xl md:text-5xl text-ink tracking-tight leading-[1.05]">Locations coming soon.</h2>
              <p className="text-ink-soft mt-4 leading-relaxed text-base md:text-lg">Meanwhile, our flagship Tarzan Villa in Hyderabad is open for reservations today.</p>
              <div className="mt-8 flex flex-wrap justify-center gap-3">
                <Link data-testid="cat-cta-book" to="/stays/tarzan-hyderabad-retreat" className="inline-flex items-center gap-2 bg-forest text-ivory px-8 py-4 rounded-full text-sm font-medium tracking-wide hover:bg-forest-700 transition-all">
                  Book Tarzan Hyderabad <ArrowRight className="h-4 w-4"/>
                </Link>
                <Link data-testid="cat-cta-partner" to="/partner" className="inline-flex items-center gap-2 border border-forest text-forest px-8 py-4 rounded-full text-sm font-medium tracking-wide hover:bg-forest hover:text-ivory transition-all">
                  Partner with us
                </Link>
              </div>
            </>
          )}
          <div className="mt-12 flex flex-wrap justify-center gap-3">
            {d.coming_soon_list.map(loc => (
              <span key={loc} className="text-[11px] uppercase tracking-[0.22em] px-4 py-2 rounded-full bg-sand-100 text-forest-700 font-semibold inline-flex items-center gap-2">
                <MapPin className="h-3 w-3" /> {loc} · Coming soon
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Explore other collections */}
      <section className="pb-24">
        <div className="container-tarzan">
          <div className="border-t border-forest/10 pt-12">
            <div className="eyebrow mb-6">Explore the other collections</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(DATA).filter(([s]) => s !== slug).map(([s, x]) => (
                <Link key={s} to={`/retreats/${s}`} data-testid={`cat-other-${s}`} className="group relative aspect-[4/3] rounded-2xl overflow-hidden soft-shadow hover:luxe-shadow transition-all">
                  <img src={x.hero} alt={x.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms]" />
                  <div className="absolute inset-0 bg-gradient-to-t from-forest-900/90 via-forest-900/30 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-4 text-ivory">
                    <div className="font-serif text-lg leading-tight">{x.name}</div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-sand-200 mt-1 inline-flex items-center gap-1 group-hover:gap-2 transition-all">Explore <ArrowRight className="h-3 w-3" /></div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
